import type { ThroughputLineConfigsListResponse } from "@situate/contracts/runtime";

export type ThroughputLineConfig = ThroughputLineConfigsListResponse["lines"][number];

/**
 * A throughput line paired with the area it belongs to. Used when listing lines from multiple
 * areas together, e.g. the Analytics page's "All Areas" filter, where the Throughput Trend
 * chart lets the user pick any configured line across every area.
 */
export interface ThroughputLineOption extends ThroughputLineConfig {
  areaId: string;
  areaName: string;
}

/** Unique selection key for a throughput line option, since a line ID is only unique within its area. */
export function getThroughputLineOptionKey(
  option: Pick<ThroughputLineOption, "areaId" | "line_id">
): string {
  return `${option.areaId}:${option.line_id}`;
}

export interface ThroughputTrendPoint {
  /** Formatted display label for the time period */
  period: string;
  /** ISO timestamp for the data point */
  timestamp: string;
  /** Ingress count (positive direction) for this period, or null if not observed. */
  in: number | null;
  /** Egress count (negative direction) for this period, or null if not observed. */
  out: number | null;
}

/** Aggregated trend data for a single configured throughput line. */
export interface ThroughputLineTrendData {
  line: ThroughputLineOption;
  points: ThroughputTrendPoint[];
  totalIn: number;
  totalOut: number;
  /** Net flow: totalIn - totalOut */
  net: number;
  loading: boolean;
  error: Error | null;
}
